/**
 * Le bloc « Lignes de l'intervention » (ADR-0016) : UN tableau, qui remplace les anciens blocs « Lignes prévues » et « Chiffrage ».
 *
 * Les pièces (palier de la gamme, pièces ajoutées) puis les lignes calculées (main d'œuvre, déplacement, forfait). Le prix est LU
 * dans le chiffrage, jamais saisi. Chaque geste — quantité, croix, flèches, ajout — réécrit la liste COMPLÈTE par l'UNIQUE chemin
 * `ecrire` (`PATCH` avec la révision), puis la page relit : aucune copie locale des lignes.
 *
 * DROITS (affichage seulement : l'API décide) : les colonnes du prix n'existent, et le chiffrage n'est appelé, que pour qui voit
 * les prix ; les gestes n'existent que pour qui gère le planning, sur une intervention ouverte.
 *
 * Les gestes sont sérialisés : tant que l'écriture n'est pas revenue ET relue (la révision a changé), les contrôles sont
 * désactivés. Un refus est dit APRÈS la relecture, dans l'en-tête du détail (jamais effacé par elle) ; une saisie illisible est dite sur place.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError, api, type BriefingIntervention, type ChiffrageInterventionVue } from '../api/client';
import { useDroitsGammes } from '../auth/useDroits';
import { texteDepuisDecimal } from '../domaine/bon-edition';
import {
  AJOUT_VIDE,
  PHRASE_HORS_TARIF,
  PHRASE_NON_FACTURE,
  ajouter,
  ajouterDeplacementEnPlus,
  bornesLigne,
  changerQuantite,
  colonnesTableau,
  construireTableau,
  corpsDeModification,
  deplacementEnPlusPossible,
  descendre,
  designationApresChoix,
  fixerQuantiteCalculee,
  monter,
  peutModifierLesLignes,
  quantiteCalculee,
  raisonEnAttention,
  retirer,
  retirerCalculee,
  sansPrixLu,
  texteQuantite,
  type CibleCalculee,
  type EntreeAjout,
  type Liste,
  type PastilleFacturation,
  type PrixLigne,
  type RangeeCalculee,
  type RangeeLigne,
  type Resultat,
} from '../domaine/lignes-intervention';
import { phraseSourceLignes } from '../domaine/planning-interventions';
import { dateFrTarif } from '../domaine/tarifs';
import { montantAffiche, phraseOrigineChiffrage, phraseRaison, raisonSeParametre } from '../domaine/tarifs-mo';
import { ChampReference } from './ChampReference';

export function LignesIntervention({
  briefing,
  rechargement,
  peutGerer,
  peutModifierLesHeures,
  onChange,
  onErreur,
}: {
  briefing: BriefingIntervention;
  rechargement: number;
  peutGerer: boolean;
  /** Le bloc « Saisir des heures » est disponible : la main d'œuvre y renvoie. */
  peutModifierLesHeures: boolean;
  /** Une écriture a réussi (ou un conflit) : la page relit la semaine, la liste et le briefing. */
  onChange: () => void;
  /** Le refus d'un geste, dit dans l'en-tête du détail ; `null` l'efface. */
  onErreur: (message: string | null) => void;
}) {
  const droits = useDroitsGammes();
  const i = briefing.intervention;
  const ecriture = peutModifierLesLignes(i, peutGerer);
  const avecPrix = droits.peutVoirPrix;

  const [chiffrage, setChiffrage] = useState<ChiffrageInterventionVue | null>(null);
  const [erreurChiffrage, setErreurChiffrage] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  /** La révision que l'écriture doit produire : tant que le briefing n'y est pas, un autre geste enverrait un jeton périmé. */
  const [revisionAttendue, setRevisionAttendue] = useState<number | null>(null);
  const [erreurLocale, setErreurLocale] = useState<string | null>(null);
  const [ajout, setAjout] = useState<EntreeAjout | null>(null);
  /** Le texte que le tarif a posé dans la désignation : lui seul se remplace en changeant de référence. */
  const designationPosee = useRef<string | null>(null);
  const enCours = envoi || (revisionAttendue !== null && i.revision < revisionAttendue);

  // Le chiffrage se relit après chaque écriture ; l'ancien reste affiché le temps de la relecture (aucun trou dans le tableau).
  useEffect(() => {
    if (!avecPrix) return;
    let annule = false;
    api
      .chiffrageIntervention(i.id)
      .then((c) => {
        if (annule) return;
        setChiffrage(c);
        setErreurChiffrage(null);
      })
      .catch((e: unknown) => {
        if (!annule) setErreurChiffrage(e instanceof ApiError ? e.message : 'Chiffrage impossible.');
      });
    return () => {
      annule = true;
    };
  }, [avecPrix, i.id, rechargement]);

  const tableau = construireTableau(briefing, chiffrage, avecPrix);
  const colonnes = colonnesTableau(avecPrix, ecriture);
  const visibles = tableau.pieces.map((r) => r.index);
  const assetId = i.asset?.id ?? null;

  /** L'UNIQUE chemin d'écriture des lignes : tout geste y passe avec la liste complète qu'il a produite. */
  const ecrire = async (r: Resultat) => {
    if (!r.ok) {
      setErreurLocale(r.message);
      return false;
    }
    if (!r.change) return true;
    setEnvoi(true);
    setErreurLocale(null);
    onErreur(null);
    try {
      const rep = await api.modifierLignesIntervention(i.id, corpsDeModification(i.revision, r.lignes));
      setRevisionAttendue(rep.revision);
      onChange();
      return true;
    } catch (e) {
      // 409 : l'intervention a changé depuis l'affichage. On relit, PUIS on le dit.
      if (e instanceof ApiError && e.status === 409) onChange();
      onErreur(e instanceof ApiError || e instanceof Error ? e.message : 'Enregistrement des lignes impossible.');
      return false;
    } finally {
      setEnvoi(false);
    }
  };

  /** Retirer (ou remettre) le déplacement automatique de CE passage : le même chemin PATCH, avec la révision. */
  const changerDeplacement = async (retire: boolean) => {
    setEnvoi(true);
    setErreurLocale(null);
    onErreur(null);
    try {
      const rep = await api.modifierIntervention(i.id, { revision: i.revision, travelWaived: retire });
      setRevisionAttendue(rep.revision);
      onChange();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) onChange();
      onErreur(e instanceof ApiError || e instanceof Error ? e.message : 'Enregistrement impossible.');
    } finally {
      setEnvoi(false);
    }
  };

  const ajouterLaPiece = async () => {
    if (ajout === null) return;
    if (await ecrire(ajouter(tableau.liste, ajout))) {
      setAjout(null);
      designationPosee.current = null;
    }
  };

  const cible = (r: RangeeCalculee): CibleCalculee => ({ genre: r.genre === 'FORFAIT' ? 'FORFAIT' : 'DEPLACEMENT', cle: r.cle, libelle: r.libelle });

  const ouvrirAjout = (mode: EntreeAjout['mode']) => {
    setErreurLocale(null);
    designationPosee.current = null;
    setAjout({ ...AJOUT_VIDE, mode });
  };

  const allerAuxHeures = () => document.getElementById('saisie-heures')?.scrollIntoView({ block: 'center' });

  return (
    <div className="lignes-intervention">
      <h3>Lignes de l’intervention</h3>
      <p className="aide">{phraseSourceLignes(briefing)}</p>
      {erreurChiffrage && <div className="message erreur">{erreurChiffrage}</div>}
      {erreurLocale && <div className="message erreur">{erreurLocale}</div>}

      <div className="tableau-defilant">
        <table className={`tableau-lignes${avecPrix ? ' avec-prix' : ''}`}>
          <thead>
            <tr>
              <th className="col-ref">Référence</th>
              <th>Désignation</th>
              <th className="col-qte">Qté</th>
              {avecPrix && <th className="col-prix">Prix unitaire</th>}
              {avecPrix && <th className="col-prix">Total HT</th>}
              {ecriture && <th className="col-act" />}
            </tr>
          </thead>
          <tbody>
            {tableau.pieces.length === 0 && (
              <tr>
                <td colSpan={colonnes.length} className="aide">
                  Aucune pièce prévue.
                </td>
              </tr>
            )}
            {tableau.pieces.map((r, rang) => (
              <tr key={r.ligne.lineKey ?? `g-${r.index}`}>
                <td className="col-ref">{r.ligne.reference ?? '—'}</td>
                <td>
                  <span className="ligne-designation">{r.ligne.designation}</span>
                  <span className={`pastille-ligne ${r.pastille.ton}`}>{r.pastille.texte}</span>
                </td>
                <td className="col-qte">
                  <Quantite
                    valeur={r.ligne.quantite}
                    unite={r.ligne.unite}
                    modifiable={ecriture && r.quantiteModifiable}
                    desactive={enCours}
                    libelle={r.ligne.designation}
                    onValider={(texte) => void ecrire(changerQuantite(tableau.liste, r.index, texte))}
                    onRevient={() => setErreurLocale(null)}
                  />
                </td>
                {avecPrix && <CellulesPrix prix={r.prix} chargement={chiffrage === null} facturation={r.facturation} />}
                {ecriture && <Gestes rangee={r} rang={rang} total={tableau.pieces.length} desactive={enCours} liste={tableau.liste} visibles={visibles} onEcrire={(res) => void ecrire(res)} />}
              </tr>
            ))}
          </tbody>
          {tableau.calculees.length > 0 && (
            <tbody className="lignes-calculees">
              <tr className="sous-titre">
                <th colSpan={colonnes.length}>Calculé d’après les heures et les tarifs</th>
              </tr>
              {tableau.calculees.map((r, n) => (
                <tr key={`${r.genre}-${n}`} className={r.prix.compte ? '' : 'ligne-remplacee'}>
                  <td className="col-ref">—</td>
                  <td>
                    <span className="ligne-designation">{r.libelle}</span>
                    {r.jour !== null && <span className="aide"> — {dateFrTarif(r.jour)}</span>}
                    {r.detail !== null && <span className="aide detail-calculee">{r.detail}</span>}
                    {r.genre === 'MAIN_D_OEUVRE' && peutModifierLesHeures && ecriture && (
                      <button type="button" className="secondaire discret bouton-heures" onClick={allerAuxHeures}>
                        Modifier les heures
                      </button>
                    )}
                  </td>
                  <td className="col-qte">
                    <Quantite
                      valeur={r.quantiteBrute}
                      unite={r.genre === 'MAIN_D_OEUVRE' ? null : r.unite}
                      modifiable={ecriture && r.modifiable}
                      desactive={enCours}
                      libelle={r.libelle}
                      brut={ecriture && r.modifiable ? null : quantiteCalculee(r)}
                      onValider={(texte) => void ecrire(fixerQuantiteCalculee(tableau.liste, cible(r), texte))}
                      onRevient={() => setErreurLocale(null)}
                    />
                  </td>
                  <CellulesPrix prix={r.prix} chargement={false} nonFacture={r.nonFacture} facturation={r.facturation} />
                  {ecriture && (
                    <td className="col-act">
                      {r.modifiable && !r.nonFacture && (
                        <button
                          type="button"
                          className="secondaire danger-discret bouton-ligne"
                          aria-label={`Ne pas facturer : ${r.libelle}`}
                          title="Ne pas facturer (quantité à 0)"
                          disabled={enCours}
                          onClick={() => void ecrire(retirerCalculee(tableau.liste, cible(r)))}
                        >
                          ✕
                        </button>
                      )}
                      {r.deplacement === 'RETIRABLE' && (
                        <button
                          type="button"
                          className="secondaire danger-discret bouton-ligne"
                          aria-label="Ne pas facturer le déplacement de ce passage"
                          title="Ne pas facturer le déplacement de ce passage"
                          disabled={enCours}
                          onClick={() => void changerDeplacement(true)}
                        >
                          ✕
                        </button>
                      )}
                      {r.deplacement === 'RETIRE_PASSAGE' && (
                        <button
                          type="button"
                          className="secondaire discret"
                          title="Facturer de nouveau le déplacement de ce passage"
                          disabled={enCours}
                          onClick={() => void changerDeplacement(false)}
                        >
                          Remettre
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>

      {ecriture && (
        <div className="ajout-ligne">
          {ajout === null ? (
            <div className="saisie-ligne">
              {assetId !== null && (
                <button type="button" className="secondaire" disabled={enCours} onClick={() => ouvrirAjout('TARIF')}>
                  + Ajouter une pièce
                </button>
              )}
              <button type="button" className="secondaire" disabled={enCours} onClick={() => ouvrirAjout('LIBRE')}>
                {assetId !== null ? '+ Pièce hors tarif (texte libre)' : '+ Ajouter une pièce (texte libre)'}
              </button>
              {deplacementEnPlusPossible(i) && (
                // Un passage de plus : il s'AJOUTE au déplacement automatique et se facture toujours, même sous contrat annuel.
                <button type="button" className="secondaire" disabled={enCours} onClick={() => void ecrire(ajouterDeplacementEnPlus(tableau.liste))}>
                  + Déplacement en plus
                </button>
              )}
            </div>
          ) : (
            <div className="ajout-ligne-saisie">
              <strong>{ajout.mode === 'TARIF' ? 'Ajouter une pièce du tarif' : 'Ajouter une pièce hors tarif'}</strong>
              <div className="saisie-ligne">
                <div className="ajout-ref">
                  {ajout.mode === 'TARIF' && assetId !== null ? (
                    <ChampReference
                      machineId={assetId}
                      libelle="Référence de la pièce à ajouter"
                      autoFocus
                      valeur={ajout.reference}
                      placeholder="Début de la référence"
                      desactive={enCours}
                      onChange={(reference) => setAjout((a) => (a ? { ...a, reference } : a))}
                      onChoisir={(choix) => {
                        // La désignation du tarif se pose si la case est vide ou ne porte que ce que le tarif y avait posé.
                        const designation = designationApresChoix(ajout.designation, designationPosee.current, choix.description);
                        if (designation === choix.description) designationPosee.current = designation;
                        setAjout({ ...ajout, reference: choix.reference, designation });
                      }}
                    />
                  ) : (
                    // Hors tarif seulement : une référence libre, sans liste (aucun tarif où chercher). Convention n° 8 : jamais pour le tarif.
                    <input
                      type="text"
                      aria-label="Référence (facultative)"
                      placeholder="Référence (facultative)"
                      maxLength={80}
                      value={ajout.reference}
                      disabled={enCours}
                      onChange={(e) => setAjout((a) => (a ? { ...a, reference: e.target.value } : a))}
                    />
                  )}
                </div>
                <input
                  type="text"
                  aria-label="Désignation de la pièce"
                  placeholder="Désignation (obligatoire)"
                  className="ajout-designation"
                  maxLength={300}
                  value={ajout.designation}
                  disabled={enCours}
                  onChange={(e) => setAjout((a) => (a ? { ...a, designation: e.target.value } : a))}
                />
                <input
                  type="text"
                  inputMode="decimal"
                  aria-label="Quantité de la pièce"
                  className="ajout-quantite"
                  value={ajout.quantite}
                  disabled={enCours}
                  onChange={(e) => setAjout((a) => (a ? { ...a, quantite: e.target.value } : a))}
                />
                <button type="button" disabled={enCours} onClick={() => void ajouterLaPiece()}>
                  Ajouter
                </button>
                <button
                  type="button"
                  className="secondaire"
                  disabled={enCours}
                  onClick={() => {
                    setAjout(null);
                    setErreurLocale(null);
                    designationPosee.current = null;
                  }}
                >
                  Annuler
                </button>
              </div>
              {ajout.mode === 'LIBRE' && <p className="aide">{PHRASE_HORS_TARIF}</p>}
            </div>
          )}
        </div>
      )}

      {tableau.resume && (
        <p className={tableau.resume.incomplet ? 'texte-attention' : ''}>
          <strong>{tableau.resume.phraseTotal}</strong>
          {tableau.resume.coutAchat !== null && <span className="aide"> — coût d’achat des pièces : {tableau.resume.coutAchat}</span>}
        </p>
      )}
      {tableau.resume?.phraseCompris && <p className="aide">{tableau.resume.phraseCompris}</p>}
      {avecPrix && chiffrage === null && !erreurChiffrage && <p className="aide">Chiffrage en cours…</p>}
      {avecPrix && chiffrage && <p className="aide">{phraseOrigineChiffrage(chiffrage)}</p>}
    </div>
  );
}

/**
 * Prix unitaire et total d'une ligne ; sans prix, la RAISON (et le lien pour paramétrer un tarif quand c'est le geste utile).
 * La pastille de facturation (« compris au contrat », « à facturer ») est décidée par le domaine : ici on ne fait que l'afficher.
 */
function CellulesPrix({
  prix,
  chargement,
  nonFacture = false,
  facturation,
}: {
  prix: PrixLigne | null;
  chargement: boolean;
  nonFacture?: boolean;
  facturation: PastilleFacturation | null;
}) {
  if (prix === null) {
    return (
      <td colSpan={2} className="col-raison aide">
        {chargement ? '…' : '—'}
      </td>
    );
  }
  const pastille = facturation && <span className={`pastille-chiffrage ${facturation.ton}`}>{facturation.texte}</span>;
  if (sansPrixLu(prix)) {
    return (
      <td colSpan={2} className={`col-raison ${raisonEnAttention(prix) ? 'texte-attention' : 'aide'}`}>
        {phraseRaison(prix.raison)}
        {raisonSeParametre(prix.raison) && (
          <>
            {' '}
            <Link to="/tarifs-main-d-oeuvre">Voulez-vous paramétrer ?</Link>
          </>
        )}
        {pastille}
      </td>
    );
  }
  return (
    <>
      <td className="col-prix">{montantAffiche(prix.prixUnitaire)}</td>
      <td className="col-prix">
        <strong>{montantAffiche(prix.total)}</strong>
        {nonFacture && <span className="aide detail-calculee">{PHRASE_NON_FACTURE}</span>}
        {pastille}
      </td>
    </>
  );
}

/** La quantité : un champ qui s'enregistre à la sortie (ou à Entrée), jamais à chaque frappe ; sinon le texte. */
function Quantite({
  valeur,
  unite,
  modifiable,
  desactive,
  libelle,
  brut = null,
  onValider,
  onRevient,
}: {
  valeur: string | null;
  unite: string | null;
  modifiable: boolean;
  desactive: boolean;
  libelle: string;
  /** Un texte déjà prêt à lire (la main d'œuvre : « 1 h 45 »). */
  brut?: string | null;
  onValider: (texte: string) => void;
  /** La saisie est revenue à la quantité enregistrée : un refus affiché pour la frappe précédente n'a plus lieu d'être. */
  onRevient: () => void;
}) {
  const affichee = texteDepuisDecimal(valeur);
  const [texte, setTexte] = useState(affichee);
  // Une relecture qui apporte une autre quantité remplace ce qui est affiché dans le champ.
  useEffect(() => setTexte(affichee), [affichee]);
  if (brut !== null) return <span>{brut}</span>;
  if (!modifiable) {
    return <span className={valeur === null ? 'aide' : ''}>{texteQuantite(valeur, unite)}</span>;
  }
  return (
    <span className="quantite-champ">
      <input
        type="text"
        inputMode="decimal"
        className="quantite"
        aria-label={`Quantité : ${libelle}`}
        placeholder={valeur === null ? 'à préciser' : undefined}
        value={texte}
        disabled={desactive}
        onChange={(e) => setTexte(e.target.value)}
        onBlur={() => {
          if (texte !== affichee) onValider(texte);
          else onRevient();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.currentTarget.blur();
          }
        }}
      />
      {unite && <span className="aide unite-quantite">{unite}</span>}
    </span>
  );
}

/** Les trois gestes d'une ligne : monter, descendre, retirer. La première ne monte pas, la dernière ne descend pas. */
function Gestes({
  rangee,
  rang,
  total,
  desactive,
  liste,
  visibles,
  onEcrire,
}: {
  rangee: RangeeLigne;
  rang: number;
  total: number;
  desactive: boolean;
  liste: Liste;
  visibles: readonly number[];
  onEcrire: (r: Resultat) => void;
}) {
  const bornes = bornesLigne(rang, total);
  const nom = rangee.ligne.designation;
  return (
    <td className="col-act">
      <span className="gestes-ligne">
        <button
          type="button"
          className="secondaire bouton-ligne"
          aria-label={`Monter la ligne ${nom}`}
          title="Monter"
          disabled={desactive || !bornes.haut}
          onClick={() => onEcrire(monter(liste, rangee.index, visibles))}
        >
          ↑
        </button>
        <button
          type="button"
          className="secondaire bouton-ligne"
          aria-label={`Descendre la ligne ${nom}`}
          title="Descendre"
          disabled={desactive || !bornes.bas}
          onClick={() => onEcrire(descendre(liste, rangee.index, visibles))}
        >
          ↓
        </button>
        <button
          type="button"
          className="secondaire danger-discret bouton-ligne"
          aria-label={`Retirer la ligne ${nom}`}
          title="Retirer la ligne"
          disabled={desactive}
          onClick={() => onEcrire(retirer(liste, rangee.index))}
        >
          ✕
        </button>
      </span>
    </td>
  );
}
